import { useEffect, useState } from 'react';
import { Alert, Button, Card, Col, Container, Form, Row, Spinner } from 'react-bootstrap';
import { Link, useParams } from 'react-router-dom';
import ClaudeCodeLoginGate from '../../components/claude-code/ClaudeCodeLoginGate';
import {
  HELP_TEXT,
  RISK_HELP,
  SKILL_PROPOSAL_SCOPE_HELP,
  SKILL_PROPOSAL_STATUS_HELP,
} from '../../components/claude-code/agentTelemetryHelp';
import { HelpBadge, HelpLabel } from '../../components/claude-code/HelpTooltip';
import SkillProposalDeliveryCard from '../../components/claude-code/SkillProposalDeliveryCard';
import SkillBundleReviewPanel from '../../components/claude-code/SkillBundleReviewPanel';
import SkillProposalSourceBadge from '../../components/claude-code/SkillProposalSourceBadge';
import { useReviewSkillProposal, useSkillProposalDetail } from '../../apps/apis/claudeCodeDashboard/hooks';
import { proposalReviewMetadata } from '../../apps/apis/claudeCodeDashboard/types';
import type {
  DashboardSession,
  SkillProposalReviewAction,
  SkillProposalRow,
  SkillProposalScope,
  SkillProposalTargeting,
} from '../../apps/apis/claudeCodeDashboard/types';
import { ROUTES } from '../../routes/routes';
import {
  buildSkillProposalReviewRequest,
  isSkillProposalReviewable,
  reviewActionsForSession,
} from './skillProposalAccessPolicy';

const SCOPE_OPTIONS: Array<{ label: string; value: SkillProposalScope }> = [
  { label: 'Personal', value: 'personal' },
  { label: 'Single role', value: 'role' },
  { label: 'Multiple roles', value: 'multi_role' },
  { label: 'Company-wide', value: 'company_wide' },
];

const statusVariant = (status: SkillProposalRow['status']) => {
  switch (status) {
    case 'approved':
      return 'success';
    case 'rejected':
      return 'secondary';
    case 'revision_requested':
      return 'warning';
    case 'converted_to_shared':
      return 'info';
    case 'delivered':
      return 'primary';
    case 'pending_review':
      return 'dark';
  }
};

const riskVariant = (risk: SkillProposalRow['riskLevel']) => {
  switch (risk) {
    case 'high':
      return 'danger';
    case 'medium':
      return 'warning';
    case 'low':
      return 'success';
  }
};

const formatDate = (value: string | null) => (value ? new Date(value).toLocaleString() : '-');

const parseRoles = (value: string) =>
  value
    .split(',')
    .map((role) => role.trim())
    .filter(Boolean);

const selectedRolesForScope = (scope: SkillProposalScope, selectedRoles: string[]) => {
  const uniqueRoles = Array.from(new Set(selectedRoles));
  if (scope === 'personal' || scope === 'company_wide') {
    return [];
  }
  if (scope === 'role') {
    return uniqueRoles.slice(0, 1);
  }
  return uniqueRoles;
};

const AgentSkillProposalDetailPage = () => {
  const params = useParams<{ proposalId: string }>();
  const proposalId = params.proposalId ? decodeURIComponent(params.proposalId) : '';

  return (
    <ClaudeCodeLoginGate>
      {(session) => (
        <Container fluid className="py-4">
          <div className="d-flex align-items-start justify-content-between gap-3 mb-3 flex-wrap">
            <div>
              <Link to={ROUTES.agentTelemetry.proposals} className="small text-muted">
                Back to proposal queue
              </Link>
              <h2 className="mb-1">
                <HelpLabel description="Review one submitted skill proposal, inspect its generated draft, and decide how it should be delivered.">
                  Agent Skill Proposal Detail
                </HelpLabel>
              </h2>
              <div className="text-muted small">
                Signed in as <strong>{session.email}</strong>
                {session.isAdmin ? <span className="ms-2 badge bg-primary">admin</span> : null}
              </div>
            </div>
          </div>
          <ProposalDetailBody proposalId={proposalId} session={session} />
        </Container>
      )}
    </ClaudeCodeLoginGate>
  );
};

interface ProposalDetailBodyProps {
  proposalId: string;
  session: DashboardSession;
}

const ProposalDetailBody = ({ proposalId, session }: ProposalDetailBodyProps) => {
  const detail = useSkillProposalDetail(proposalId, { enabled: proposalId.length > 0 });
  const review = useReviewSkillProposal();
  const [note, setNote] = useState('');
  const [scope, setScope] = useState<SkillProposalScope>('personal');
  const [selectedRolesText, setSelectedRolesText] = useState('');

  useEffect(() => {
    if (!detail.data) {
      return;
    }
    setScope(detail.data.targeting.scope);
    setSelectedRolesText(detail.data.targeting.selectedRoles.join(', '));
  }, [detail.data]);

  if (!proposalId) {
    return <Alert variant="danger">Missing proposal id.</Alert>;
  }

  if (detail.isLoading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <Spinner animation="border" />
      </div>
    );
  }

  if (detail.error) {
    return <Alert variant="danger">Failed to load proposal detail: {(detail.error as Error).message}</Alert>;
  }

  if (!detail.data) {
    return <Alert variant="warning">Proposal detail was not returned.</Alert>;
  }

  const selectedRoles = selectedRolesForScope(scope, parseRoles(selectedRolesText));
  const assets = detail.data.assets?.length ? detail.data.assets : detail.data.asset ? [detail.data.asset] : [];
  const visibleReviewActions = reviewActionsForSession(session, detail.data.reviewActions);
  const canReview = visibleReviewActions.length > 0 && isSkillProposalReviewable(detail.data.proposal.status);

  const handleReview = async (action: SkillProposalReviewAction) => {
    const trimmedNote = note.trim();
    await review.mutateAsync(
      buildSkillProposalReviewRequest({
        proposalId,
        session,
        action,
        note: trimmedNote,
        finalScope: scope,
        selectedRoles,
      }),
    );
    await detail.refetch();
  };

  return (
    <Row className="g-3">
      <Col xl={5}>
        <ProposalSummaryCard proposal={detail.data.proposal} targeting={detail.data.targeting} />
        <ProposalReviewMetadataCard detail={detail.data} />
        <SkillProposalDeliveryCard delivery={detail.data.delivery} />
        <ReviewCard
          canReview={canReview}
          isSubmitting={review.isPending}
          note={note}
          scope={scope}
          selectedRolesText={selectedRolesText}
          visibleActions={visibleReviewActions}
          isAdmin={session.isAdmin}
          setNote={setNote}
          setScope={setScope}
          setSelectedRolesText={setSelectedRolesText}
          onReview={handleReview}
        />
      </Col>
      <Col xl={7}>
        <SkillBundleReviewPanel assets={assets} title="Bundle source review" />
      </Col>
    </Row>
  );
};

function ProposalReviewMetadataCard({ detail }: { detail: ReturnType<typeof useSkillProposalDetail>['data'] }) {
  if (!detail) {
    return null;
  }
  const metadata = proposalReviewMetadata(detail);
  return (
    <Card className="mb-3">
      <Card.Body>
        <Card.Title className="h6">Review provenance</Card.Title>
        <Row className="g-2 small">
          <Col md={6}>
            <span className="text-muted">Adapters: </span>
            {metadata.adapters.join(', ') || '-'}
          </Col>
          <Col md={6} className="text-break">
            <span className="text-muted">Root hash: </span>
            <span className="font-monospace">{metadata.rootHashes.join(', ') || '-'}</span>
          </Col>
          <Col md={6}>
            <span className="text-muted">Source role asset: </span>
            {metadata.sourceRoleAssetId ?? '-'}
          </Col>
          <Col md={6}>
            <span className="text-muted">Pinned base version: </span>
            {metadata.baseVersion ?? '-'}
          </Col>
          <Col md={12} className="text-break">
            <span className="text-muted">Pinned base root hash: </span>
            <span className="font-monospace">{metadata.baseBundleHash ?? '-'}</span>
          </Col>
        </Row>
        <div className="small text-muted mt-3">
          Scanner findings show kind, file, and line only; source text is never copied into finding metadata.
        </div>
        {metadata.riskFindings.length ? (
          <ul className="small mb-0 mt-2">
            {metadata.riskFindings.map((finding) => (
              <li key={`${finding.kind}:${finding.file}:${finding.line}`}>
                {finding.severity ? `${finding.severity} · ` : ''}
                {finding.kind} · {finding.file}:{finding.line ?? '-'}
              </li>
            ))}
          </ul>
        ) : (
          <div className="small mt-2">No scanner findings were returned.</div>
        )}
      </Card.Body>
    </Card>
  );
}

interface ProposalSummaryCardProps {
  proposal: SkillProposalRow;
  targeting: SkillProposalTargeting;
}

const ProposalSummaryCard = ({ proposal, targeting }: ProposalSummaryCardProps) => (
  <Card className="mb-3">
    <Card.Body>
      <div className="d-flex justify-content-between gap-2 align-items-start mb-2">
        <div>
          <h4 className="mb-1">{proposal.title}</h4>
          <div className="text-muted small">
            {proposal.employeeId ? (
              <Link to={ROUTES.agentTelemetry.employeeDetail(proposal.employeeId)}>{proposal.employeeId}</Link>
            ) : (
              'Role-level proposal'
            )}
          </div>
        </div>
        <div className="d-flex gap-1 flex-wrap justify-content-end">
          <SkillProposalSourceBadge source={proposal.proposalSource} />
          <HelpBadge bg={statusVariant(proposal.status)} description={SKILL_PROPOSAL_STATUS_HELP[proposal.status]}>
            {proposal.status}
          </HelpBadge>
          <HelpBadge bg={riskVariant(proposal.riskLevel)} description={RISK_HELP[proposal.riskLevel]}>
            risk: {proposal.riskLevel}
          </HelpBadge>
        </div>
      </div>

      <p>{proposal.summary}</p>

      <Row className="g-2 small mb-3">
        <Col md={6}>
          <div className="text-muted">
            <HelpLabel description={HELP_TEXT.proposalType}>Type</HelpLabel>
          </div>
          <div>{proposal.proposalType}</div>
        </Col>
        <Col md={6}>
          <div className="text-muted">
            <HelpLabel description={HELP_TEXT.proposalSource}>Source</HelpLabel>
          </div>
          <div>{proposal.proposalSource ?? 'telemetry_batch'}</div>
        </Col>
        <Col md={6}>
          <div className="text-muted">
            <HelpLabel description={HELP_TEXT.proposalTarget}>Target</HelpLabel>
          </div>
          <div>
            {proposal.targetAdapter}:{proposal.targetSkillId ?? 'new-skill'}
          </div>
        </Col>
        <Col md={6}>
          <div className="text-muted">
            <HelpLabel description={HELP_TEXT.confidence}>Confidence</HelpLabel>
          </div>
          <div>{Math.round(proposal.confidence * 100)}%</div>
        </Col>
        <Col md={6}>
          <div className="text-muted">
            <HelpLabel description="Safe telemetry event count and session count behind this proposal.">
              Events / sessions
            </HelpLabel>
          </div>
          <div>
            {proposal.sourceEventCount.toLocaleString()} / {proposal.sourceSessionCount.toLocaleString()}
          </div>
        </Col>
        <Col md={6}>
          <div className="text-muted">
            <HelpLabel description="Last time this proposal record changed.">Updated</HelpLabel>
          </div>
          <div>{formatDate(proposal.updatedAt)}</div>
        </Col>
        <Col md={6}>
          <div className="text-muted">
            <HelpLabel description="Employee id of the person who last reviewed this proposal.">Reviewed by</HelpLabel>
          </div>
          <div>{proposal.reviewerEmployeeId ?? '-'}</div>
        </Col>
      </Row>

      <div className="p-3 rounded bg-light border mb-3">
        <div className="text-muted small mb-1">
          <HelpLabel description={HELP_TEXT.safeEvidence}>Safe evidence summary</HelpLabel>
        </div>
        <div className="small">{proposal.evidenceSummary}</div>
      </div>

      <div className="p-3 rounded border mb-3">
        <div className="text-muted small mb-1">
          <HelpLabel description={HELP_TEXT.recommendedAction}>Recommended action</HelpLabel>
        </div>
        <div className="small">{proposal.recommendedAction}</div>
      </div>

      <div className="p-3 rounded border">
        <div className="text-muted small mb-2">
          <HelpLabel description={HELP_TEXT.roleTargeting}>Role targeting context</HelpLabel>
        </div>
        <Row className="g-2 small">
          <Col md={6}>
            <div className="text-muted">
              <HelpLabel description={SKILL_PROPOSAL_SCOPE_HELP[targeting.scope]}>Derived scope</HelpLabel>
            </div>
            <div>{targeting.scope}</div>
          </Col>
          <Col md={6}>
            <div className="text-muted">
              <HelpLabel description="Roles selected by the submitting employee when the proposal was created.">
                Source roles
              </HelpLabel>
            </div>
            <div>{targeting.sourceEmployeeRoles.length ? targeting.sourceEmployeeRoles.join(', ') : '-'}</div>
          </Col>
        </Row>
        <div className="mt-2">
          {targeting.recommendedRoles.length ? (
            targeting.recommendedRoles.map((role) => (
              <div key={role.role} className="small mb-1">
                <HelpBadge
                  bg="info"
                  className="me-2"
                  description="Recommended role target and generator confidence for sharing this proposal."
                >
                  {role.role} {Math.round(role.confidence * 100)}%
                </HelpBadge>
                {role.reason}
              </div>
            ))
          ) : (
            <div className="small text-muted">No role recommendation is attached.</div>
          )}
        </div>
      </div>
    </Card.Body>
  </Card>
);

interface ReviewCardProps {
  canReview: boolean;
  isSubmitting: boolean;
  note: string;
  scope: SkillProposalScope;
  selectedRolesText: string;
  visibleActions: SkillProposalReviewAction[];
  isAdmin: boolean;
  setNote: (value: string) => void;
  setScope: (value: SkillProposalScope) => void;
  setSelectedRolesText: (value: string) => void;
  onReview: (action: SkillProposalReviewAction) => Promise<void>;
}

const ReviewCard = ({
  canReview,
  isSubmitting,
  note,
  scope,
  selectedRolesText,
  visibleActions,
  isAdmin,
  setNote,
  setScope,
  setSelectedRolesText,
  onReview,
}: ReviewCardProps) => (
  <Card>
    <Card.Body>
      <Card.Title className="h5">
        <HelpLabel description={HELP_TEXT.reviewAction}>
          {isAdmin ? 'Review action' : 'Personal confirmation'}
        </HelpLabel>
      </Card.Title>
      {isAdmin ? (
        <>
          <Form.Group className="mb-3">
            <Form.Label className="small text-muted">
              <HelpLabel description={HELP_TEXT.finalScope}>Final scope</HelpLabel>
            </Form.Label>
            <Form.Select
              value={scope}
              onChange={(event) => setScope(event.target.value as SkillProposalScope)}
              disabled={!canReview}
            >
              {SCOPE_OPTIONS.map((option) => (
                <option value={option.value} key={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label className="small text-muted">
              <HelpLabel description={HELP_TEXT.selectedRoles}>Selected roles</HelpLabel>
            </Form.Label>
            <Form.Control
              value={selectedRolesText}
              onChange={(event) => setSelectedRolesText(event.target.value)}
              placeholder="development, growth-data"
              disabled={!canReview || scope === 'personal' || scope === 'company_wide'}
            />
            <Form.Text>
              Comma-separated. This is stored as structured targeting and is the source of truth for shared assets.
            </Form.Text>
          </Form.Group>
        </>
      ) : (
        <Alert variant="info">
          Confirm this proposal for your own local agent setup, or request revision if the generated skill does not
          match your workflow.
        </Alert>
      )}
      <Form.Group className="mb-3">
        <Form.Label className="small text-muted">
          <HelpLabel description={HELP_TEXT.reviewNote}>Review note</HelpLabel>
        </Form.Label>
        <Form.Control
          as="textarea"
          rows={4}
          value={note}
          onChange={(event) => setNote(event.target.value)}
          disabled={!canReview}
        />
      </Form.Group>
      {canReview ? (
        <div className="d-flex gap-2 flex-wrap">
          {visibleActions.includes('approve') ? (
            <Button variant="success" disabled={isSubmitting} onClick={() => onReview('approve')}>
              Approve personal
            </Button>
          ) : null}
          {visibleActions.includes('convert_to_shared') ? (
            <Button variant="outline-info" disabled={isSubmitting} onClick={() => onReview('convert_to_shared')}>
              Share / role asset
            </Button>
          ) : null}
          {visibleActions.includes('request_revision') ? (
            <Button variant="outline-secondary" disabled={isSubmitting} onClick={() => onReview('request_revision')}>
              Request revision
            </Button>
          ) : null}
          {visibleActions.includes('reject') ? (
            <Button variant="outline-danger" disabled={isSubmitting} onClick={() => onReview('reject')}>
              Reject
            </Button>
          ) : null}
        </div>
      ) : (
        <Alert variant="secondary" className="mb-0">
          This proposal is no longer pending review.
        </Alert>
      )}
    </Card.Body>
  </Card>
);

export default AgentSkillProposalDetailPage;
